import React, { FC, useState, useEffect } from "react";
import { GetAllSession } from "../../../../../api/ai/types";
import { useNavigate } from "react-router-dom";
import { useGetBusinessClientSessionAnalysis } from "../../../../../hooks/ai/useAI";
import { usePDF } from "@react-pdf/renderer";
import AnalyticsPDFDocument from "../../../pages/FiBi/voice/components/AnalyticsPDFDocument";

interface Props {
  data: GetAllSession;
  isDownload?: boolean;
  buid?: string;
}

const FibiSessionAction: FC<Props> = ({ data, isDownload, buid }) => {
  const navigate = useNavigate();
  const sessionCount = (data.id + 1).toString();

  const onView = () => {
    navigate(
      `/fibi-voice-interview-prep?userType=fibiUser&sessionCount=${sessionCount}`
    );
  };

  const {
    data: sessionAnalysisData,
    refetch: fetchSessionAnalysis,
    isFetching,
  } = useGetBusinessClientSessionAnalysis(sessionCount, buid || "", false);

  const [isGenerating, setIsGenerating] = useState(false);
  const [shouldAutoDownload, setShouldAutoDownload] = useState(false);

  const [instance, updateInstance] = usePDF({
    document: undefined,
  });

  useEffect(() => {
    if (sessionAnalysisData && shouldAutoDownload) {
      const pdfDoc = (
        <AnalyticsPDFDocument sessionAnalysisData={sessionAnalysisData} />
      );
      updateInstance(pdfDoc);
    }
  }, [sessionAnalysisData, shouldAutoDownload, updateInstance]);

  useEffect(() => {
    if (shouldAutoDownload && instance.url) {
      const link = document.createElement("a");
      link.href = instance.url;
      link.download = `interview-analytics-session-${sessionCount}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setShouldAutoDownload(false);
    }
  }, [shouldAutoDownload, instance.url, sessionCount]);

  const handleDownloadClick = async () => {
    if (!sessionAnalysisData) {
      setIsGenerating(true);
      setShouldAutoDownload(true);
      try {
        await fetchSessionAnalysis();
      } catch (error) {
        setShouldAutoDownload(false);
      } finally {
        setIsGenerating(false);
      }
    } else {
      setShouldAutoDownload(true);
      const pdfDoc = (
        <AnalyticsPDFDocument sessionAnalysisData={sessionAnalysisData} />
      );
      updateInstance(pdfDoc);
    }
  };

  if (isDownload) {
    if (sessionAnalysisData && instance.url) {
      return (
        <a
          href={instance.url}
          download={`interview-analytics-session-${sessionCount}.pdf`}
          className="text-[#0076E9]! hover:underline transition-all"
        >
          Download PDF
        </a>
      );
    }

    if (sessionAnalysisData && instance.loading) {
      return (
        <span className="text-gray-400! cursor-not-allowed">
          Preparing PDF...
        </span>
      );
    }

    if (sessionAnalysisData && instance.error) {
      return <span className="text-red-500!">PDF Error: {instance.error}</span>;
    }

    if (isFetching || isGenerating) {
      return (
        <span className="text-gray-400! cursor-not-allowed">
          Generating PDF...
        </span>
      );
    }

    return (
      <button
        className="text-[#0076E9]! hover:underline transition-all"
        onClick={handleDownloadClick}
      >
        Download PDF
      </button>
    );
  }

  return (
    <button
      className="text-[#0076E9]! hover:underline transition-all"
      onClick={onView}
    >
      View
    </button>
  );
};

export default FibiSessionAction;
